import React, { useState, useEffect, useMemo } from "react";
import {
    HiOutlineTrash,
    HiOutlineRefresh,
    HiOutlineExclamationCircle,
    HiOutlineFolder,
    HiOutlineDocumentText,
    HiOutlineSearch,
    HiOutlineEmojiSad,
    HiOutlineFilter,
    HiOutlineCheckCircle,
    HiCheckCircle,
    HiX
} from "react-icons/hi";
import { motion, AnimatePresence } from "framer-motion";
import {
    getTrashAPI,
    recoverFileAPI,
    recoverFolderAPI,
    permanentDeleteFileAPI,
    permanentDeleteFolderAPI
} from "../services/driveService";
import { toast } from "react-toastify";
import FileIcon from "../Components/FileIcon";
import { useActivity } from "../ActivityContext";
import PermanentDeleteModal from "../Components/PermanentDeleteModal";
import useAuth from "../useAuth";
import { server } from "../server";

const TrashPage = () => {
    const { refreshActivityReport } = useActivity();
    const [trashData, setTrashData] = useState({ folders: [], files: [] });
    const [loading, setLoading] = useState(true);
    const [processingId, setProcessingId] = useState(null);
    const [searchQuery, setSearchQuery] = useState("");
    const [sortBy, setSortBy] = useState(() => localStorage.getItem("trash_sortBy") || "name");
    const [sortOrder, setSortOrder] = useState(() => localStorage.getItem("trash_sortOrder") || "asc");
    const { user } = useAuth();

    // Modal state
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [itemToDelete, setItemToDelete] = useState(null);
    const [selectedItems, setSelectedItems] = useState([]);

    const handleToggleSelect = (item, type) => {
        const itemKey = `${type}:${item.id}`;
        setSelectedItems(prev =>
            prev.includes(itemKey)
                ? prev.filter(k => k !== itemKey)
                : [...prev, itemKey]
        );
    };

    const handleSelectAll = () => {
        const allKeys = filteredItems.map(item => `${item.type}:${item.id}`);
        if (selectedItems.length === allKeys.length && allKeys.length > 0) {
            setSelectedItems([]);
        } else {
            setSelectedItems(allKeys);
        }
    };

    const handleBatchRestore = async () => {
        const count = selectedItems.length;
        if (count === 0) return;

        toast.info(`Restoring ${count} items...`);
        let successCount = 0;

        for (const itemKey of selectedItems) {
            const [type, id] = itemKey.split(':');
            const item = type === 'folder'
                ? trashData.folders.find(f => String(f.id) === String(id))
                : trashData.files.find(f => String(f.id) === String(id));

            if (item) {
                try {
                    if (type === 'file') {
                        await recoverFileAPI(item.id);
                    } else {
                        await recoverFolderAPI(item.id);
                    }
                    successCount++;
                } catch (error) {
                    console.error(`Failed to restore ${item.name}`, error);
                }
            }
        }

        toast.success(`${successCount} items recovered successfully!`);
        setSelectedItems([]);
        refreshActivityReport();
        fetchTrash();
    };

    const handleBatchPermanentDelete = () => {
        const count = selectedItems.length;
        if (count === 0) return;

        setItemToDelete({ id: "batch", name: `${count} selected items`, itemType: "batch" });
        setIsDeleteModalOpen(true);
    };

    const fetchTrash = async () => {
        setLoading(true);
        try {
            console.log("Fetching trash with sort:", sortBy, sortOrder);
            const data = await getTrashAPI(sortBy, sortOrder);
            setTrashData(data);
        } catch (error) {
            console.error(error);
            toast.error("Failed to load trash");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        localStorage.setItem("trash_sortBy", sortBy);
        localStorage.setItem("trash_sortOrder", sortOrder);
        fetchTrash();
    }, [sortBy, sortOrder]);

    const formatFileSize = (bytes) => {
        if (!bytes || bytes === 0) return "0 B";
        const k = 1024;
        const sizes = ["B", "KB", "MB", "GB", "TB"];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
    };

    const handleRestore = async (item, type) => {
        setProcessingId(item.id);
        try {
            if (type === 'file') {
                await recoverFileAPI(item.id);
            } else {
                await recoverFolderAPI(item.id);
            }
            toast.success(`${item.name} recovered successfully!`);
            refreshActivityReport();
            fetchTrash();
        } catch (error) {
            toast.error(error?.message || "Failed to recover item");
        } finally {
            setProcessingId(null);
        }
    };

    const handlePermanentDelete = (item, type) => {
        setItemToDelete({ ...item, itemType: type });
        setIsDeleteModalOpen(true);
    };

    const confirmPermanentDelete = async (item, type) => {
        setProcessingId(item.id);
        try {
            if (type === 'batch') {
                let successCount = 0;
                for (const itemKey of selectedItems) {
                    const [itemType, itemId] = itemKey.split(':');
                    try {
                        if (itemType === 'file') {
                            await permanentDeleteFileAPI(itemId);
                        } else {
                            await permanentDeleteFolderAPI(itemId);
                        }
                        successCount++;
                    } catch (err) {
                        console.error(`Batch delete failed for ${itemKey}`, err);
                    }
                }
                toast.success(`${successCount} items deleted permanently`);
                setSelectedItems([]);
            } else {
                if (type === 'file') {
                    await permanentDeleteFileAPI(item.id);
                } else {
                    await permanentDeleteFolderAPI(item.id);
                }
                toast.success(`${item.name} deleted permanently`);
            }
            refreshActivityReport();
            fetchTrash();
            setIsDeleteModalOpen(false);
        } catch (error) {
            toast.error(error?.message || "Failed to delete item permanently");
        } finally {
            setProcessingId(null);
        }
    };

    const filteredItems = useMemo(() => [
        ...trashData.folders.map(f => ({ ...f, type: 'folder' })),
        ...trashData.files.map(f => ({ ...f, type: 'file' }))
    ].filter(item => item.name.toLowerCase().includes(searchQuery.toLowerCase())), [trashData, searchQuery]);

    const totalSelectedSize = useMemo(() => {
        let total = 0;
        selectedItems.forEach(key => {
            const [type, id] = key.split(':');
            const item = type === 'folder'
                ? trashData.folders.find(f => String(f.id) === String(id))
                : trashData.files.find(f => String(f.id) === String(id));
            if (item) total += (item.size || 0);
        });
        return total;
    }, [selectedItems, trashData]);

    return (
        <div className="space-y-4 animate-in fade-in slide-in-from-bottom-4 duration-700">
            {/* Header Section - Compact */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 px-2">
                <div className="flex flex-col gap-1">
                    <h1 className="text-3xl font-extrabold text-slate-900 tracking-tight flex items-center gap-3">
                        Trash
                        <span className="px-2 py-0.5 bg-rose-50 text-rose-500 rounded-lg text-[9px] font-black uppercase tracking-widest border border-rose-100">
                            Security Zone
                        </span>
                    </h1>
                    <p className="text-slate-400 text-[10px] font-bold uppercase tracking-[0.2em]">Manage your cloud data graveyard</p>
                </div>

                {/* Search Bar - Slim */}
                <div className="flex items-center gap-2">
                    <div className="flex items-center gap-1 bg-slate-100/80 p-1 rounded-xl border border-slate-200/60 shadow-sm backdrop-blur-sm">
                        <div className="flex items-center pl-2.5 pr-1 border-r border-slate-200/80 gap-2">
                            {loading ? (
                                <div className="w-3.5 h-3.5 border-2 border-rose-600 border-t-transparent rounded-full animate-spin" />
                            ) : (
                                <HiOutlineFilter size={13} className="text-slate-400" />
                            )}
                            <select
                                value={sortBy}
                                onChange={(e) => setSortBy(e.target.value)}
                                className="bg-transparent text-[10px] font-black uppercase tracking-wider focus:outline-none cursor-pointer text-slate-700 hover:text-rose-600 transition-colors appearance-none pr-4 relative"
                                style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' fill=\'none\' viewBox=\'0 0 24 24\' stroke=\'%23e11d48\'%3E%3Cpath stroke-linecap=\'round\' stroke-linejoin=\'round\' stroke-width=\'2\' d=\'M19 9l-7 7-7-7\'%3E%3C/path%3E%3C/svg%3E")', backgroundRepeat: 'no-repeat', backgroundPosition: 'right center', backgroundSize: '10px' }}
                            >
                                <option value="name">Name</option>
                                <option value="date">Date</option>
                                <option value="size">Size</option>
                                <option value="type">Type</option>
                            </select>
                        </div>
                        <div className="px-2">
                            <select
                                value={sortOrder}
                                onChange={(e) => setSortOrder(e.target.value)}
                                className="bg-transparent text-[10px] font-black uppercase tracking-wider focus:outline-none cursor-pointer text-slate-700 hover:text-rose-600 transition-colors appearance-none pr-4"
                                style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' fill=\'none\' viewBox=\'0 0 24 24\' stroke=\'%23e11d48\'%3E%3Cpath stroke-linecap=\'round\' stroke-linejoin=\'round\' stroke-width=\'2\' d=\'M19 9l-7 7-7-7\'%3E%3C/path%3E%3C/svg%3E")', backgroundRepeat: 'no-repeat', backgroundPosition: 'right center', backgroundSize: '10px' }}
                            >
                                <option value="asc">ASC</option>
                                <option value="desc">DESC</option>
                            </select>
                        </div>
                    </div>

                    <div className="relative group min-w-[280px]">
                        <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-slate-400">
                            <HiOutlineSearch size={16} />
                        </div>
                        <input
                            type="text"
                            placeholder="Search trash..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="w-full pl-10 pr-4 py-2.5 bg-white border border-slate-200/60 rounded-xl focus:outline-none focus:ring-4 focus:ring-rose-50/50 focus:border-rose-600 transition-all text-xs font-bold text-slate-900"
                        />
                    </div>

                    {/* Multi-Selection Actions Bar */}
                    <AnimatePresence>
                        {selectedItems.length > 0 && (
                            <motion.div
                                initial={{ opacity: 0, x: 20 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: 20 }}
                                className="flex items-center gap-1 bg-rose-600 p-1 rounded-xl shadow-lg shadow-rose-100"
                            >
                                <div className="px-3 py-1 flex items-center gap-2 border-r border-white/20">
                                    <HiOutlineCheckCircle className="text-white" size={16} />
                                    <div className="flex flex-col leading-tight">
                                        <span className="text-[10px] font-black text-white uppercase tracking-widest">{selectedItems.length} SELECTED</span>
                                        <span className="text-[8px] font-bold text-rose-200 uppercase tracking-tighter">{formatFileSize(totalSelectedSize)}</span>
                                    </div>
                                </div>
                                <button
                                    onClick={handleBatchRestore}
                                    className="p-2 text-white hover:bg-white/10 rounded-lg transition-all"
                                    title="Restore Selected"
                                >
                                    <HiOutlineRefresh size={18} />
                                </button>
                                {user?.userType === "admin" && <button
                                    onClick={handleBatchPermanentDelete}
                                    className="p-2 text-white hover:bg-white/10 rounded-lg transition-all"
                                    title="Delete Selected Permanently"
                                >
                                    <HiOutlineTrash size={18} />
                                </button>}
                                <button
                                    onClick={() => setSelectedItems([])}
                                    className="p-2 text-white hover:bg-white/10 rounded-lg transition-all border-l border-white/20"
                                    title="Clear Selection"
                                >
                                    <HiX size={16} />
                                </button>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>
            </div>



            {/* Content Section - High Density Table with Scroll */}
            <div className="bg-white rounded-2xl overflow-x-auto fancy-scrollbar flex flex-col border border-slate-200/60 shadow-xl min-h-[400px]">
                <div className="min-w-[700px] flex flex-col flex-1">
                    {/* Table Header */}
                    {filteredItems.length > 0 && (
                        <div className="flex items-center px-6 py-2 border-b border-slate-200 text-[9px] font-black uppercase tracking-[0.2em] text-slate-400 bg-slate-50/50 sticky top-0 z-10 backdrop-blur-md">
                            <div className="w-10 flex items-center justify-center">
                                <button
                                    onClick={handleSelectAll}
                                    className={`w-4 h-4 rounded border transition-all flex items-center justify-center ${selectedItems.length === filteredItems.length && filteredItems.length > 0
                                        ? "bg-rose-500 border-rose-500 text-white"
                                        : "border-slate-300 hover:border-rose-400 bg-white"
                                        }`}
                                >
                                    {selectedItems.length === filteredItems.length && filteredItems.length > 0 && <HiCheckCircle size={12} />}
                                </button>
                            </div>
                            <div className="flex-1 flex items-center gap-3 ml-2">
                                <span className="w-8"></span> {/* Icon Spacer */}
                                <span>Item Name</span>
                            </div>
                            <div className="w-48 hidden md:block">Deleted On</div>
                            <div className="w-32 hidden lg:block">Type</div>
                            <div className="w-24 text-right">Size</div>
                            <div className="w-24"></div> {/* Actions Spacer */}
                        </div>
                    )}

                    {loading ? (
                        <div className="flex-1 flex flex-col items-center justify-center gap-4 text-slate-400 py-32">
                            <div className="w-8 h-8 border-3 border-rose-100 border-t-rose-500 rounded-full animate-spin" />
                            <p className="font-bold tracking-widest text-[9px] uppercase italic">Analysing graveyard...</p>
                        </div>
                    ) : filteredItems.length === 0 ? (
                        <div className="flex-1 flex flex-col items-center justify-center gap-6 text-slate-400 py-32">
                            <div className="w-16 h-16 bg-slate-50 rounded-full flex items-center justify-center text-slate-200">
                                <HiOutlineEmojiSad size={32} />
                            </div>
                            <p className="font-bold tracking-widest text-[9px] uppercase">Trash is empty and clean</p>
                        </div>
                    ) : (
                        <div className="divide-y divide-slate-100/50 flex-1">
                            <AnimatePresence mode="popLayout">
                                {filteredItems.map((item, idx) => (
                                    <motion.div
                                        initial={{ opacity: 0, y: 5 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, scale: 0.98 }}
                                        transition={{ delay: idx * 0.02 }}
                                        key={item.id}
                                        className={`px-6 py-1.5 transition-all flex items-center group cursor-default ${selectedItems.includes(`${item.type}:${item.id}`) ? "bg-rose-50/50 shadow-[inset_4px_0_0_0_#e11d48]" : "hover:bg-rose-50/30"}`}
                                    >
                                        <div className="w-10 flex items-center justify-center">
                                            <button
                                                onClick={(e) => { e.stopPropagation(); handleToggleSelect(item, item.type); }}
                                                className={`w-4 h-4 rounded border transition-all flex items-center justify-center ${selectedItems.includes(`${item.type}:${item.id}`)
                                                    ? "bg-rose-500 border-rose-500 text-white"
                                                    : "border-slate-200 group-hover:border-rose-300 bg-white"
                                                    }`}
                                            >
                                                {selectedItems.includes(`${item.type}:${item.id}`) && <HiCheckCircle size={12} />}
                                            </button>
                                        </div>
                                        <div className="flex-1 flex items-center gap-3 min-w-0 ml-2">
                                            <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center transition-all group-hover:scale-105 group-hover:bg-white group-hover:shadow-sm overflow-hidden">
                                                {item.type === 'folder' ? (
                                                    <HiOutlineFolder size={18} className="text-amber-500" />
                                                ) : item.thumbnailUrl ? (
                                                    <img
                                                        src={`${server.replace('/api/v1', '')}${item.thumbnailUrl}`}
                                                        alt={item.name}
                                                        className="w-full h-full object-contain p-0.5 rounded-sm"
                                                    />
                                                ) : (
                                                    <FileIcon extension={item.contentType?.split('/')[1] || 'file'} className="w-4 h-4" />
                                                )}
                                            </div>
                                            <span className="font-bold text-xs text-slate-800 truncate" title={item.name}>{item.name}</span>
                                        </div>

                                        <div className="w-48 hidden md:block text-[11px] font-medium text-slate-500">
                                            {new Date(item.updatedAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
                                        </div>

                                        <div className="w-32 hidden lg:block">
                                            <span className={`inline-block px-2 py-0.5 rounded-md text-[8px] font-black uppercase tracking-wider border ${item.type === 'folder' ? 'bg-amber-50 text-amber-600 border-amber-100' : 'bg-indigo-50 text-indigo-600 border-indigo-100'
                                                }`}>
                                                {item.type}
                                            </span>
                                        </div>

                                        <div className="w-24 text-right text-[11px] font-bold text-slate-400">
                                            {item.type === 'file' ? formatFileSize(item.size) : '---'}
                                        </div>

                                        <div className="w-24 ml-4 flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-all">
                                            <button
                                                disabled={processingId === item.id}
                                                onClick={() => handleRestore(item, item.type)}
                                                className="p-1 px-1.5 rounded-md hover:bg-white hover:shadow-sm text-emerald-500 transition-all"
                                                title="Restore"
                                            >
                                                <HiOutlineRefresh size={14} className={processingId === item.id ? "animate-spin" : ""} />
                                            </button>
                                            {user?.userType === "admin" && <button
                                                disabled={processingId === item.id}
                                                onClick={() => handlePermanentDelete(item, item.type)}
                                                className="p-1 px-1.5 rounded-md hover:bg-white hover:shadow-sm text-rose-500 transition-all"
                                                title="Delete Permanently"
                                            >
                                                <HiOutlineTrash size={14} />
                                            </button>}
                                        </div>
                                    </motion.div>
                                ))}
                            </AnimatePresence>
                        </div>
                    )}
                </div>
            </div>



            <PermanentDeleteModal
                isOpen={isDeleteModalOpen}
                onClose={() => setIsDeleteModalOpen(false)}
                item={itemToDelete}
                type={itemToDelete?.itemType}
                onConfirm={confirmPermanentDelete}
                isProcessing={processingId === itemToDelete?.id}
            />
        </div>
    );
};

export default TrashPage;
